const TYPING_CHUNK_SIZE = 6;
const TYPING_DELAY_MS = 20;
const SCROLL_EVERY_CHUNKS = 3;

type UseTypewriterAnimationOptions<TTarget> = {
  getText: (target: TTarget) => string;
  setText: (target: TTarget, text: string) => void;
};
/**
 * Generic typewriter/ellipsis animation composable for mutable text targets.
 *
 * @template TTarget Shape of the object whose text should be animated.
 * @param scrollCallback Called during animation updates to keep the latest content visible.
 * @param options Accessors that read/write text on the target object.
 * @param options.getText Returns the current text value from the target.
 * @param options.setText Persists a new text value onto the target.
 * @returns Animation controls.
 * @returns returns.startEllipsisAnimation Starts a loading animation (`baseText...`) on a target.
 * @returns returns.stopEllipsisAnimation Stops any active loading animation interval.
 * @returns returns.typeText Streams `fullText` onto a target in chunks for a typing effect.
 * @returns returns.cleanup Alias for stop logic; call on component unmount.
 */

export const useTypewriterAnimation = <TTarget>(
  scrollCallback: () => void,
  { getText, setText }: UseTypewriterAnimationOptions<TTarget>
) => {
  let isTypingStopped = false;

  // Keep a single loading interval alive at a time so repeated requests do not leak timers.
  let ellipsisInterval: ReturnType<typeof setInterval> | null = null;

  const startEllipsisAnimation = (target: TTarget, baseText: string) => {
    stopEllipsisAnimation();

    let dotCount = 0;
    ellipsisInterval = setInterval(() => {
      dotCount = (dotCount + 1) % 4; // Cycle through 0, 1, 2, 3
      setText(target, baseText + '.'.repeat(dotCount));
      scrollCallback();
    }, 400);
  };

  const stopEllipsisAnimation = () => {
    if (ellipsisInterval) {
      clearInterval(ellipsisInterval);
      ellipsisInterval = null;
    }
  };

  const stopTyping = () => {
    isTypingStopped = true;
  };

  const typeText = async ({ target, fullText }: { target: TTarget; fullText: string }) => {
    // Ensure loading dots never overlap with streamed typing output.
    stopEllipsisAnimation();
    isTypingStopped = false;
    setText(target, '');
    let chunkCounter = 0;

    for (let i = 0; i < fullText.length; i += TYPING_CHUNK_SIZE) {
      if (isTypingStopped) break;

      const nextChunk = fullText.slice(i, i + TYPING_CHUNK_SIZE);
      setText(target, getText(target) + nextChunk);
      chunkCounter += 1;

      if (chunkCounter % SCROLL_EVERY_CHUNKS === 0) {
        // Avoid forcing layout on every chunk while still keeping the newest text visible.
        scrollCallback();
      }

      await new Promise((resolve) => setTimeout(resolve, TYPING_DELAY_MS));
    }

    if (!isTypingStopped) {
      scrollCallback();
    }
  };

  const cleanup = () => {
    // Hook into component unmount to avoid orphaned intervals.
    stopEllipsisAnimation();
    stopTyping();
  };

  return {
    startEllipsisAnimation,
    stopEllipsisAnimation,
    typeText,
    stopTyping,
    cleanup,
  };
};
